/* =========================================================
   VFW Platform Finder — the "one card is always open" router
   (James, 2026-07-24). The finder must read the same way on
   every device: exactly ONE card shows its dim + Shop/Explore
   overlay at any moment, so the interaction is self-evident.

   · Default: the first card (Corvette) is active on load — the
     buttons + overlay are already showing, so nobody has to
     guess how to interact.
   · Hover devices: moving the pointer onto another card makes
     THAT card active; leaving the grid returns to the default
     first card.
   · Keyboard: focusing a card's button opens that card; leaving
     the grid returns to the default.
   · Touch / no-hover: the first tap on a closed card OPENS it
     (reveals the overlay) instead of navigating; once open, its
     Shop/Explore buttons work on the next tap. Tapping a
     different card moves the open state to it.

   Pure progressive enhancement: with no JS the first card is
   already `.is-active` server-side and CSS `:hover`/`:focus-within`
   still reveal the rest. Reduced motion is handled in CSS.
   ========================================================= */

(() => {
  if (customElements.get('vfw-platform-grid')) return;

  class VfwPlatformGrid extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('.vfw-pf__card'));
      if (!this.cards.length) return;

      this.hoverable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      // No card starts open: the always-open default kept its photo
      // permanently under the dark button overlay on every device.
      // Hover/focus/tap opens; leaving the grid closes everything.

      this.cards.forEach((card) => {
        // Hover devices follow the pointer.
        card.addEventListener('mouseenter', () => {
          if (this.hoverable) this.activate(card);
        });

        // Keyboard: opening happens when a link inside the card gains focus.
        card.addEventListener('focusin', () => this.activate(card));

        // Touch / no-hover: first tap opens, it doesn't navigate.
        // EXCEPT on phones (≤600px): there the overlay is gone entirely and
        // the meta band carries always-visible Explore/Shop links (James's
        // under-card design, 2026-07-30) — taps must pass straight through.
        card.addEventListener('click', (event) => {
          if (this.hoverable) return; // real cursors navigate normally
          if (window.matchMedia('(max-width: 600px)').matches) {
            // Phones: the photograph and the name are the biggest targets on
            // the card — a tap anywhere that is NOT one of the real links
            // follows the platform's Explore destination (James, 2026-07-30:
            // "if I click on Corvette it doesn't take me anywhere").
            if (event.target.closest('a')) return;
            const url = card.dataset.landingUrl;
            if (url) window.location.href = url;
            return;
          }
          if (card.classList.contains('is-active')) return; // already open → let the link work
          if (event.target.closest('a')) event.preventDefault();
          this.activate(card);
        });
      });

      // Leaving the whole grid closes every card.
      this.addEventListener('mouseleave', () => {
        if (this.hoverable) this.activate(null);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.activate(null);
      });
    }

    activate(card) {
      if (this.current === card) return;
      this.cards.forEach((c) => c.classList.toggle('is-active', c === card));
      this.current = card;
    }
  }

  customElements.define('vfw-platform-grid', VfwPlatformGrid);
})();
